import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type {
  Product,
  PutStoneSettingRequest,
  StoneSetting,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useProductScope } from '../hooks/use-product-scope';
import {
  useConfigurator,
  useRemoveConfigurator,
  useSetConfigurator,
} from '../hooks/use-stone-setting';

import { SharedChip } from './product-scope';

type Shape = PutStoneSettingRequest['acceptedShapes'][number];
type StoneType = PutStoneSettingRequest['acceptedStoneTypes'][number];

const SHAPES: Shape[] = [
  'round',
  'oval',
  'emerald',
  'cushion',
  'pear',
  'marquise',
  'princess',
  'radiant',
  'asscher',
  'heart',
  'rose',
];
const STONE_TYPES: StoneType[] = ['lab_diamond', 'natural_diamond', 'gemstone'];

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** Stones the setting would take that shoppers can't see because no markup band prices them. */
function hasHiddenStones(setting: StoneSetting) {
  return setting.stonePriceRuleCount === 0 || setting.unpricedStoneCount > 0;
}

interface DiamondDraft {
  enabled: boolean;
  shapes: ReadonlySet<Shape>;
  stoneTypes: ReadonlySet<StoneType>;
  caratMin: string;
  caratMax: string;
}

function toDraft(setting: StoneSetting | null): DiamondDraft {
  return {
    enabled: setting !== null,
    shapes: new Set(setting?.acceptedShapes ?? []),
    stoneTypes: new Set(setting?.acceptedStoneTypes ?? ['lab_diamond', 'natural_diamond']),
    caratMin: setting?.caratMin != null ? String(setting.caratMin) : '',
    caratMax: setting?.caratMax != null ? String(setting.caratMax) : '',
  };
}

const toCarat = (value: string) => (value === '' ? null : Number(value));

const sameSet = <T,>(a: ReadonlySet<T>, b: ReadonlySet<T>) =>
  a.size === b.size && [...a].every((value) => b.has(value));

/** What's behind a switched-off selector doesn't count: it is neither shown nor saved. */
function sameDraft(a: DiamondDraft, b: DiamondDraft) {
  if (a.enabled !== b.enabled) return false;
  if (!a.enabled) return true;
  return (
    sameSet(a.shapes, b.shapes) &&
    sameSet(a.stoneTypes, b.stoneTypes) &&
    toCarat(a.caratMin) === toCarat(b.caratMin) &&
    toCarat(a.caratMax) === toCarat(b.caratMax)
  );
}

export interface ConfiguratorSectionProps {
  product: Product;
}

/** Whether shoppers choose the center diamond, and from what. */
export function ConfiguratorSection({ product }: ConfiguratorSectionProps) {
  const { t } = useTranslation('products');
  const { can } = usePermissions();
  const { readOnly } = useProductScope();
  const query = useConfigurator(product.id);

  // On the data, not the query's status: a failed refetch must not take unsaved edits away.
  if (query.data !== undefined && !readOnly && can('configurator.write')) {
    return <DiamondEditor productId={product.id} setting={query.data} />;
  }

  let body: ReactNode;
  if (query.isPending) body = <Skeleton className="h-5 w-56" />;
  else if (query.data === undefined) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else body = <DiamondSummary setting={query.data} />;

  return (
    <SummaryCard title={t('configurator.title')} badge={<SharedChip />}>
      {body}
    </SummaryCard>
  );
}

/** For someone who may only read. */
function DiamondSummary({ setting }: { setting: StoneSetting | null }) {
  const { t } = useTranslation('products');
  if (!setting) return <span>{t('diamondCard.off')}</span>;
  const { caratMin: min, caratMax: max } = setting;
  const carat =
    min != null && max != null
      ? t('diamondCard.caratRange', { min, max })
      : min != null
        ? t('diamondCard.caratFrom', { min })
        : max != null
          ? t('diamondCard.caratUpTo', { max })
          : null;
  const shapes = SHAPES.filter((shape) => setting.acceptedShapes.includes(shape)).map((shape) =>
    t(`configurator.shape.${shape}`),
  );
  return (
    <>
      <span>{[t('diamondCard.on'), shapes.join(', '), carat].filter(Boolean).join(' · ')}</span>
      {hasHiddenStones(setting) ? (
        <span className="bg-destructive/10 text-destructive w-fit rounded-md px-1.5 py-0.5 text-xs">
          {setting.stonePriceRuleCount === 0
            ? t('diamondCard.noBands')
            : t('diamondCard.hidden', { count: setting.unpricedStoneCount })}
        </span>
      ) : null}
    </>
  );
}

function Choice({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        'flex h-8 cursor-pointer items-center gap-2 rounded-md border px-2 text-[13px] font-medium whitespace-nowrap',
        checked && 'border-primary/50 bg-primary-tint',
      )}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      {children}
    </label>
  );
}

function CaratInput({
  label,
  shortLabel,
  value,
  invalid,
  onChange,
}: {
  label: string;
  shortLabel: string;
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('products');
  const id = useId();
  return (
    <div className="relative min-w-0 flex-1">
      <Label htmlFor={id} className="sr-only">
        {label}
      </Label>
      <span
        aria-hidden
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-xs"
      >
        {shortLabel}
      </span>
      <Input
        id={id}
        type="number"
        min={0}
        max={100}
        step="0.01"
        value={value}
        placeholder={t('configurator.noLimit')}
        aria-invalid={invalid || undefined}
        className="h-8 [appearance:textfield] pr-7 pl-9 text-[13px] [&::-webkit-inner-spin-button]:appearance-none"
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-xs">
        {t('diamondCard.caratUnit')}
      </span>
    </div>
  );
}

/** The card as a form on the page's save bar, written to its own endpoint apart from the product. */
function DiamondEditor({
  productId,
  setting,
}: {
  productId: string;
  setting: StoneSetting | null;
}) {
  const { t } = useTranslation(['products', 'common']);
  const setConfigurator = useSetConfigurator(productId);
  const removeConfigurator = useRemoveConfigurator(productId);
  const saving = setConfigurator.isPending || removeConfigurator.isPending;
  const message = useErrorMessage();
  const failed = setConfigurator.error ?? removeConfigurator.error;
  // Everything else is toasted globally; a validation failure is left to the card.
  const rejected = isApiError(failed) && failed.kind === 'validation';

  const [draft, setDraft] = useState(() => toDraft(setting));
  const [saveTried, setSaveTried] = useState(false);
  const change = (patch: Partial<DiamondDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  // A newer setting replaces a clean draft; one with edits keeps them, and is dirty if they differ.
  const [seenSetting, setSeenSetting] = useState(setting);
  if (seenSetting !== setting) {
    setSeenSetting(setting);
    if (sameDraft(draft, toDraft(seenSetting))) setDraft(toDraft(setting));
  }

  const min = toCarat(draft.caratMin);
  const max = toCarat(draft.caratMax);
  const emptyChoice = draft.shapes.size === 0 || draft.stoneTypes.size === 0;
  const caratsCrossed = min !== null && max !== null && min > max;
  const invalid = draft.enabled && (emptyChoice || caratsCrossed);
  const invalidMessage = emptyChoice
    ? t('products:configurator.pickAtLeastOne')
    : t('products:diamondCard.caratsCrossed');

  const dirty = !sameDraft(draft, toDraft(setting));
  const save = async () => {
    if (invalid) {
      setSaveTried(true);
      // Save sits in the top bar, so this card may be out of sight.
      toast.error(invalidMessage);
      return;
    }
    try {
      if (draft.enabled) {
        await setConfigurator.mutateAsync({
          acceptedShapes: [...draft.shapes],
          acceptedStoneTypes: [...draft.stoneTypes],
          caratMin: min,
          caratMax: max,
        });
        toast.success(t('products:configurator.toasts.saved'));
      } else {
        await removeConfigurator.mutateAsync();
        toast.success(t('products:configurator.toasts.removed'));
      }
      setSaveTried(false);
    } catch {
      // Stays dirty; `failed` above has it.
    }
  };

  useSaveBarEntry({
    dirty,
    saving,
    onSave: save,
    onDiscard: () => {
      setDraft(toDraft(setting));
      setSaveTried(false);
      setConfigurator.reset();
      removeConfigurator.reset();
    },
  });

  return (
    <SummaryCard
      title={t('products:configurator.title')}
      badge={<SharedChip />}
      description={t('products:diamondCard.description')}
      action={
        <label className="flex cursor-pointer items-center gap-2 text-[13px]">
          <span className="text-muted-foreground">
            {draft.enabled ? t('products:diamondCard.on') : t('products:diamondCard.off')}
          </span>
          <Switch
            aria-label={t('products:diamondCard.toggle')}
            checked={draft.enabled}
            onCheckedChange={(enabled) => change({ enabled })}
          />
        </label>
      }
    >
      {draft.enabled ? (
        <div className="mt-1 flex flex-col gap-3.5 border-t pt-3.5">
          {setting && hasHiddenStones(setting) ? (
            <Alert variant="destructive" role="status">
              <AlertTriangle aria-hidden />
              <AlertTitle className="line-clamp-none">
                {setting.stonePriceRuleCount === 0
                  ? t('products:configurator.pricing.noBands')
                  : t('products:configurator.pricing.unpriced', {
                      count: setting.unpricedStoneCount,
                    })}
              </AlertTitle>
              <AlertDescription>
                <span>{t('products:configurator.pricing.hint')}</span>
                <Link to="/org/stone-pricing" className="font-medium underline">
                  {t('products:configurator.pricing.open')}
                </Link>
              </AlertDescription>
            </Alert>
          ) : null}
          <fieldset>
            <legend className="mb-1.5 text-[13px] font-semibold">
              {t('products:configurator.shapes')}
            </legend>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-1.5">
              {SHAPES.map((shape) => (
                <Choice
                  key={shape}
                  checked={draft.shapes.has(shape)}
                  onToggle={() => change({ shapes: toggle(draft.shapes, shape) })}
                >
                  {t(`products:configurator.shape.${shape}`)}
                </Choice>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-wrap items-start gap-x-6 gap-y-3.5">
            <fieldset className="min-w-0">
              <legend className="mb-1.5 text-[13px] font-semibold">
                {t('products:configurator.stoneTypes')}
              </legend>
              <div className="flex flex-wrap gap-1.5">
                {STONE_TYPES.map((type) => (
                  <Choice
                    key={type}
                    checked={draft.stoneTypes.has(type)}
                    onToggle={() => change({ stoneTypes: toggle(draft.stoneTypes, type) })}
                  >
                    {t(`products:configurator.stoneType.${type}`)}
                  </Choice>
                ))}
              </div>
            </fieldset>
            <fieldset className="min-w-52 flex-1">
              <legend className="mb-1.5 text-[13px] font-semibold">
                {t('products:diamondCard.caratRangeTitle')}
              </legend>
              <div className="flex gap-1.5">
                <CaratInput
                  label={t('products:diamondCard.caratMin')}
                  shortLabel={t('products:diamondCard.caratMinShort')}
                  value={draft.caratMin}
                  invalid={caratsCrossed}
                  onChange={(caratMin) => change({ caratMin })}
                />
                <CaratInput
                  label={t('products:diamondCard.caratMax')}
                  shortLabel={t('products:diamondCard.caratMaxShort')}
                  value={draft.caratMax}
                  invalid={caratsCrossed}
                  onChange={(caratMax) => change({ caratMax })}
                />
              </div>
            </fieldset>
          </div>
          {invalid ? (
            <p
              role={saveTried ? 'alert' : undefined}
              className={cn('text-xs', saveTried ? 'text-destructive' : 'text-muted-foreground')}
            >
              {invalidMessage}
            </p>
          ) : null}
        </div>
      ) : null}
      {rejected ? (
        <p role="alert" className="text-destructive text-xs">
          {message(failed)}
        </p>
      ) : null}
    </SummaryCard>
  );
}
